import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import Keycloak from 'keycloak-js';
import { Observable } from 'rxjs';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';
import { SERVICE_LOOKUP } from 'src/environments/global.config';

export const RECIPIENT_TYPE_OPTIONS = [
	{ name: 'Tenant', value: 'TENANT' },
	{ name: 'User', value: 'USER' },
	{ name: 'Service', value: 'SERVICE' },
] as const;
export type NotificationRecipientType = (typeof RECIPIENT_TYPE_OPTIONS)[number]['value'];

export const SEVERITY_TYPE_OPTIONS = [
	{ name: 'Info', value: 'INFO' },
	{ name: 'Warning', value: 'WARNING' },
	{ name: 'Critical', value: 'CRITICAL' },
] as const;
export type NotificationType = (typeof SEVERITY_TYPE_OPTIONS)[number]['value'];

export interface Notification {
	id: string;
	title: string;
	message: string;
	recipientType: NotificationRecipientType;
	recipientName: string;
	readAt: number | null;
	severity: NotificationType;
	expiredAt: number;
	createdAt: number;
	createdBy: string;
}

export interface NotificationDialogData {
	notification?: Notification;
	dialogType: 'update';
}

@Injectable({
	providedIn: 'root',
})
export class NotificationService {
	protected readonly api: string = environment.api.notification;

	constructor(
		protected readonly http: HttpClient,
		private readonly keycloak: Keycloak,
		private readonly userService: UserService
	) {
		const value = SERVICE_LOOKUP('notification');
		if (value != null && value.endpoint !== this.api) {
			this.api = value.endpoint;
		}
	}

	public async getNotifications(allAttributes: boolean = false): Promise<Notification[]> {
		let params;
		if (allAttributes) {
			params = new HttpParams().set('allAttributes', allAttributes);
		}
		return this.http.get<Notification[]>(`${this.api}/v1/Notifications`, { params }).toPromise();
	}

	public async describeNotification(id: string): Promise<Notification> {
		return this.http.get<Notification>(`${this.api}/v1/Notifications/${id}`).toPromise();
	}

	public async deleteNotification(id: string): Promise<any> {
		return this.http.delete<any>(`${this.api}/v1/Notifications/${id}`).toPromise();
	}

	public async createNotification(notification: Partial<Notification>): Promise<Notification> {
		return this.http.post<Notification>(`${this.api}/v1/Notifications`, notification).toPromise();
	}

	public async updateNotification(id: string, notification: Partial<Notification>): Promise<Notification> {
		return this.http.put<Notification>(`${this.api}/v1/Notifications/${id}`, notification).toPromise();
	}

	public async getDeletedNotifications(allAttributes: boolean = false): Promise<Notification[]> {
		let params;
		if (allAttributes) {
			params = new HttpParams().set('allAttributes', allAttributes);
		}
		return this.http.get<Notification[]>(`${this.api}/v1/Notifications/deleted`, { params }).toPromise();
	}

	public async updateReadStatus(id: string, is_read: boolean): Promise<Notification> {
		return this.http.put<Notification>(`${this.api}/v1/Notifications/read/${id}`, { is_read }).toPromise();
	}

	public async recoverNotification(id: string, notification: Partial<Notification> = {}): Promise<Notification> {
		return this.http.put<Notification>(`${this.api}/v1/Notifications/delete/${id}`, notification).toPromise();
	}

	public streamNotifications(token?: string): Observable<{ event: string; notification_id: string }> {
		return new Observable((observer) => {
			const ctrl = new AbortController();
			const authToken = token ?? this.userService.getSession()?.jwt ?? this.keycloak.token;

			fetchEventSource(`${this.api}/v1/Notifications/stream`, {
				headers: { Authorization: `Bearer ${authToken}` },
				signal: ctrl.signal,
				onmessage: (event) => {
					if (event.data) observer.next(JSON.parse(event.data));
				},
				onerror: (err) => {
					observer.error(err);
					throw err;
				},
			});

			return () => ctrl.abort();
		});
	}
}
